import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLiveSpread } from "@/hooks/useLiveSpread";
import SetupGate from "@/components/SetupGate";
import BgFx from "@/components/BgFx";
import BrandLogo from "@/components/BrandLogo";
import NewHedgeModal from "@/components/NewHedgeModal";
import NotificationBell from "@/components/NotificationBell";
import HealthStatusBar from "@/components/HealthStatusBar";
import KillSwitchButton from "@/components/KillSwitchButton";
import ExecutionHealthBadge from "@/components/ExecutionHealthBadge";
import DailyPnlPill from "@/components/DailyPnlPill";
import "./Layout.css";

const NAV_OPERATION = [
  {
    to: "/dashboard",
    label: "Panel",
    testId: "nav-dashboard",
    icon: (
      <svg viewBox="0 0 24 24">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
  {
    to: "/signals",
    label: "Sinyaller",
    testId: "nav-signals",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M4 14a8 8 0 0 1 8-8M4 18a12 12 0 0 1 12-12" />
        <circle cx="5" cy="19" r="1.3" fill="currentColor" stroke="none" />
      </svg>
    ),
    countKey: "pending_signals",
  },
  {
    to: "/positions",
    label: "Pozisyonlar",
    testId: "nav-positions",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M7 7l-3 3 3 3M4 10h13a3 3 0 0 1 0 6h-1" />
        <path d="M17 17l3-3-3-3" />
      </svg>
    ),
    countKey: "open_positions",
  },
  {
    to: "/scenario",
    label: "Senaryo",
    testId: "nav-scenario",
    badge: "Simüle",
    icon: (
      <svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: "/analytics",
    label: "Analiz",
    testId: "nav-analytics",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M3 3v18h18" strokeLinecap="round" />
        <path d="M7 14l4-4 3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const NAV_ACCOUNT = [
  {
    to: "/balance",
    label: "Bakiye",
    testId: "nav-balance",
    icon: (
      <svg viewBox="0 0 24 24">
        <rect x="3" y="6" width="18" height="13" rx="2" />
        <path d="M3 10h18" />
      </svg>
    ),
  },
  {
    to: "/strategy",
    label: "Strateji",
    testId: "nav-strategy",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6l-8-4z" />
      </svg>
    ),
  },
  {
    to: "/ai",
    label: "AI Asistan",
    testId: "nav-ai",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" strokeLinecap="round" />
        <circle cx="12" cy="12" r="3.5" />
      </svg>
    ),
  },
  {
    to: "/settings",
    label: "Ayarlar",
    testId: "nav-settings",
    icon: (
      <svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 13a7.6 7.6 0 0 0 0-2l2-1.6-2-3.4-2.4 1a7 7 0 0 0-1.7-1L15 2.5h-4l-.3 2.5a7 7 0 0 0-1.7 1l-2.4-1-2 3.4 2 1.6a7.6 7.6 0 0 0 0 2l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 1.7 1l.3 2.5h4l.3-2.5a7 7 0 0 0 1.7-1l2.4 1 2-3.4z" />
      </svg>
    ),
  },
];

const ADMIN_NAV = {
  to: "/admin",
  label: "Admin",
  testId: "nav-admin",
  icon: (
    <svg viewBox="0 0 24 24">
      <path d="M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6l-8-4z" />
      <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const VAULT_NAV = {
  to: "/vault",
  label: "Kasa",
  testId: "nav-vault",
  icon: (
    <svg viewBox="0 0 24 24">
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      <circle cx="12" cy="15.5" r="1.2" fill="currentColor" stroke="none" />
      <path d="M12 16.7v2" strokeLinecap="round" />
    </svg>
  ),
};

const SECURITY_NAV = {
  to: "/security",
  label: "Güvenlik",
  testId: "nav-security",
  icon: (
    <svg viewBox="0 0 24 24">
      <path d="M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6l-8-4z" />
      <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
    </svg>
  ),
};

const PAGE_TITLES = {
  "/dashboard": "Panel",
  "/signals": "Sinyaller",
  "/positions": "Pozisyonlar",
  "/scenario": "Senaryo",
  "/pairs": "Pariteler",
  "/analytics": "Analiz",
  "/balance": "Bakiye",
  "/strategy": "Strateji",
  "/ai": "AI Asistan",
  "/settings": "Ayarlar",
  "/admin": "Admin",
  "/vault": "Kasa",
  "/security": "Güvenlik Merkezi",
};

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [hedgeOpen, setHedgeOpen] = useState(false);

  const { data: stats } = useQuery({
    queryKey: ["stats"],
    queryFn: api.stats,
    refetchInterval: 15000,
  });
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
  });
  const symbol = settings?.signal_symbol || "ETHUSDT";
  const { live, connected: liveOn } = useLiveSpread(symbol);
  const { data: prices } = useQuery({
    queryKey: ["prices", symbol],
    queryFn: () => api.prices(symbol),
    refetchInterval: liveOn ? 30000 : 5000,
    enabled: !!settings,
  });

  const darkexPrice = liveOn && live?.darkex ? live.darkex.mid : prices?.darkex?.price;
  const spreadPct = liveOn && live ? live.spread_pct : prices?.best_spread_pct;

  const pageTitle =
    Object.entries(PAGE_TITLES).find(([key]) =>
      location.pathname.startsWith(key)
    )?.[1] || "Panel";

  const bonusPct = Math.round((settings?.default_bonus_rate || 0.1) * 100);
  const tierLabel = settings?.tier || "Regular";
  const tierFillPct = Math.min(100, Math.max(0, bonusPct * 6.4));
  const counts = {
    pending_signals: stats?.pending_signals || 0,
    open_positions: stats?.open_positions || 0,
  };
  const userInitial = (user?.username || "?").slice(0, 1).toUpperCase();
  const isAdmin = user?.role === "admin";
  const openPosCount = counts.open_positions;

  // ⌨️ Global klavye kısayolları (trader workflow hızlandırma)
  //   n             → Yeni Hedge modal'ı aç
  //   g + d / s / p → Dashboard / Signals / Positions navigasyon
  //   Ctrl+Shift+K  → Acil Durdurma (Kill Switch) modal'ı (admin only)
  //   Esc           → modal/popover'lar kendi içinde dinler
  useEffect(() => {
    let gPressed = false;
    let gTimer = null;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const editable = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if (editable) return;
      // Ctrl+Shift+K → kill switch (admin only)
      if (e.ctrlKey && e.shiftKey && (e.key === "K" || e.key === "k")) {
        if (isAdmin && openPosCount > 0) {
          e.preventDefault();
          document.querySelector('[data-testid="kill-switch-btn"]')?.click();
        }
        return;
      }
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        setHedgeOpen(true);
        return;
      }
      // "g" prefix + harf
      if (e.key === "g" || e.key === "G") {
        gPressed = true;
        clearTimeout(gTimer);
        gTimer = setTimeout(() => { gPressed = false; }, 800);
        return;
      }
      if (gPressed) {
        gPressed = false;
        clearTimeout(gTimer);
        const k = e.key.toLowerCase();
        if (k === "d") navigate("/dashboard");
        else if (k === "s") navigate("/signals");
        else if (k === "p") navigate("/positions");
        else if (k === "a") navigate("/analytics");
        else if (k === "b") navigate("/balance");
        else return;
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(gTimer);
    };
  }, [isAdmin, openPosCount, navigate]);

  return (
    <div className="App">
      <BgFx />
      <div className="tx-app">
        {/* ===== Sidebar ===== */}
        <aside className="tx-side" data-testid="sidebar">
          <div className="tx-brand">
            <div className="mark">
              <svg viewBox="0 0 24 24">
                <path d="M13 2 4.5 13.5H11L9.5 22 19 9.5h-6.5L13 2z" />
              </svg>
            </div>
            <BrandLogo size="md" loop={true} eyebrow="Darkex Hedge" align="center" />
          </div>

          <nav className="tx-nav">
            <span className="sec">Operasyon</span>
            {NAV_OPERATION.map((item) => (
              <NavItem key={item.to} item={item} count={counts[item.countKey]} />
            ))}
            <span className="sec">Hesap</span>
            {NAV_ACCOUNT.map((item) => (
              <NavItem key={item.to} item={item} />
            ))}
            {isAdmin && <NavItem item={ADMIN_NAV} accent />}
            {isAdmin && <NavItem item={VAULT_NAV} accent />}
            {isAdmin && <NavItem item={SECURITY_NAV} accent />}
          </nav>

          <div className="tx-side-foot">
            <div className="tx-sf-live">
              <span className="d" /> Canlı · Açık Veri
            </div>
            <div className="tx-tier">
              <div className="t">
                <span>
                  Tier · <b>{tierLabel}</b>
                </span>
                <span>%{bonusPct}</span>
              </div>
              <div className="bar">
                <i style={{ width: `${tierFillPct}%` }} />
              </div>
            </div>
            <button
              type="button"
              className="tx-acct"
              onClick={logout}
              data-testid="logout-btn"
              title="Çıkış yap"
            >
              <span className="av">
                <span className="d">{userInitial}</span>
                <span>
                  @{user?.username || "—"}
                  {isAdmin && <span className="role">ADMIN</span>}
                </span>
              </span>
              <span className="go">↗</span>
            </button>
          </div>
        </aside>

        {/* ===== Main ===== */}
        <main className="tx-main">
          <header className="tx-topbar" data-testid="topbar">
            <div className="tx-tb-l">
              <h1 data-testid="topbar-title">{pageTitle}</h1>
              <span className="tx-pill">Kayıp Kurtarma · %{bonusPct}</span>
            </div>
            <div className="tx-tb-r">
              <div className="tx-stat">
                <div className="k">
                  Darkex
                  {liveOn && (
                    <span
                      className="tx-live-dot"
                      data-testid="topbar-live-dot"
                      title={`Canlı akış · Darkex ${live?.darkex?.age_ms ?? "—"}ms · Binance ${live?.binance?.age_ms ?? "—"}ms`}
                    />
                  )}
                </div>
                <div className="v" data-testid="topbar-darkex-price">
                  {darkexPrice != null
                    ? `$${Number(darkexPrice).toFixed(2)}`
                    : "—"}
                </div>
              </div>
              <div className="tx-stat">
                <div className="k">Spread</div>
                <div
                  className={`v ${(spreadPct || 0) >= 0 ? "up" : "down"}`}
                  data-testid="topbar-spread"
                >
                  {spreadPct != null ? `${Number(spreadPct).toFixed(3)}%` : "—"}
                </div>
              </div>
              <div className="tx-stat">
                <div className="k">Net K/Z</div>
                <div
                  className={`v ${
                    (stats?.total_net_pnl || 0) >= 0 ? "up" : "down"
                  }`}
                  data-testid="topbar-net-pnl"
                >
                  {stats?.total_net_pnl != null
                    ? `${stats.total_net_pnl >= 0 ? "+" : ""}$${Number(
                        stats.total_net_pnl
                      ).toFixed(2)}`
                    : "—"}
                </div>
              </div>
              <NotificationBell />
              <DailyPnlPill />
              <ExecutionHealthBadge />
              {isAdmin && openPosCount > 0 && (
                <KillSwitchButton
                  openCount={openPosCount}
                  isAdmin={isAdmin}
                />
              )}
              <button
                type="button"
                className="tx-new-btn"
                onClick={() => setHedgeOpen(true)}
                data-testid="topbar-new-hedge-btn"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                </svg>
                Yeni Hedge
              </button>
            </div>
          </header>

          <div className="tx-wrap" data-testid="page-wrap">
            <HealthStatusBar />
            <SetupGate>{children}</SetupGate>
          </div>
        </main>
      </div>
      <NewHedgeModal open={hedgeOpen} onClose={() => setHedgeOpen(false)} />
    </div>
  );
};

function NavItem({ item, count, accent }) {
  return (
    <NavLink
      to={item.to}
      data-testid={item.testId}
      className={({ isActive }) => (isActive ? "on" : "")}
      style={accent ? { color: "var(--acc-2)" } : undefined}
    >
      {item.icon}
      <span>{item.label}</span>
      {item.badge && <span className="tag tag-badge">{item.badge}</span>}
      {count > 0 && <span className="tag">{count}</span>}
    </NavLink>
  );
}
